import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';
import { apiService } from '../../../services/api';
import Icon from '../shared/Icon';
import NotificationsPopover from './NotificationsPopover';
import EditProfileModal from '../Account/EditProfileModal';
import GetHelpModal from '../Account/GetHelpModal';
import { initialsStyle, resolveAvatarKind } from '../Account/avatar';
import { useCapabilities } from '../../../utils/capabilities';
import './AppHeader.css';

const POLL_INTERVAL_MS = 30000;

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const AppHeader = ({ pageTitle, user, className = '', onRefreshUser, onShowNotification, onLogout }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // PRD-09: prefer the user's editable display_name, fall back to the SSO name.
  const displayName = user?.profile?.display_name || user?.name || 'User';
  // PRD-09: subtitle is the user's self-declared Title (job_title), NOT the RBAC role.
  const titleText = user?.profile?.job_title || '';
  const initials = getInitials(displayName);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);
  // PRD-09 account menu (avatar dropdown) + the merged Edit Profile modal.
  const [accountOpen, setAccountOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const accountRef = useRef(null);
  // PRD-651: gates the full account menu (Control / Get help / Sign out) and the
  // sidebar-logout removal. Flag off (default) = pre-PRD-651 behaviour, so rollback
  // restores the original sidebar logout control.
  const accountMenuFull = useFlag('account-menu');
  const navigate = useNavigate();
  // PRD-1416: "Switch account" is the super-admin org switcher (lives in Settings >
  // Organization). Only super admins can switch, so the item is shown only to them —
  // a menu row that did nothing for everyone else is worse than omitting it.
  // PRD-1699: with `role-capabilities` on, the bell and Update profile follow the role.
  // Without Access Notifications nothing is fetched either, not just hidden (E11).
  // The switcher is on the Organization tab, so Switch account needs that tab too.
  const caps = useCapabilities(user?.features);
  const canSwitchAccount = !!user?.is_super_admin
    && (!caps.enabled || caps.has('editOrganization') || caps.has('manageSso'));
  const showNotifications = !caps.enabled || caps.has('notifications');
  const showUpdateProfile = !caps.enabled || caps.has('updateProfile');
  // A popover left open as the bell went must not reappear by itself if it comes back.
  useEffect(() => {
    if (!showNotifications) setDropdownOpen(false);
  }, [showNotifications]);
  // PRD-09: render the avatar per its chosen source — photo, org logo, or coloured
  // initials. Photo/logo are server-proxied, fetched as object URLs (the render endpoints
  // need auth and an <img> can't carry the header).
  const [avatarUrl, setAvatarUrl] = useState(null);
  const avatarKind = resolveAvatarKind(user?.profile, !!user?.org_logo_url);
  const avatarColor = user?.profile?.avatar_color || '';

  const fetchUnreadCount = async () => {
    try {
      const list = await apiService.getNotifications({ unreadOnly: true, limit: 100 });
      setUnreadCount(Array.isArray(list) ? list.length : 0);
    } catch {
      setUnreadCount(0);
    }
  };

  const fetchAllNotifications = async () => {
    try {
      const list = await apiService.getNotifications({ unreadOnly: false, limit: 50 });
      setNotifications(Array.isArray(list) ? list : []);
    } catch {
      setNotifications([]);
    }
  };

  useEffect(() => {
    if (!showNotifications) return undefined;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    // Refetch as soon as the tab regains focus / becomes visible, so the unread
    // dot appears promptly on new notifications instead of only on manual reload.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchUnreadCount);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchUnreadCount);
    };
  }, [showNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      fetchAllNotifications();
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };
    const handleEscape = (e) => e.key === 'Escape' && setAccountOpen(false);
    if (accountOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [accountOpen]);

  useEffect(() => {
    let cancelled = false;
    let created = null;
    const fetcher =
      avatarKind === 'photo'
        ? apiService.fetchProfilePhotoObjectUrl
        : avatarKind === 'logo'
        ? apiService.fetchOrgLogoObjectUrl
        : null;
    if (fetcher) {
      fetcher().then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setAvatarUrl(u);
      });
    } else {
      setAvatarUrl(null);
    }
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [user, avatarKind]);

  const handleBellClick = () => {
    setDropdownOpen((prev) => !prev);
  };

  // Escape from the popover returns focus to the bell (spec).
  const handleCloseNotifications = () => {
    setDropdownOpen(false);
    bellRef.current?.focus();
  };

  const handleMarkRead = async (id) => {
    try {
      await apiService.markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      fetchUnreadCount();
    } catch (e) {
      console.warn('Mark notification read failed:', e);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiService.markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.warn('Mark all notifications read failed:', e);
    }
  };

  const bellLabel = unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';

  return (
    <header className={`header ${className}`.trim()}>
      <h2 className="header-title">{pageTitle || ''}</h2>
      <div className="user-info">
        {showNotifications && (
        <div className="notifications" ref={dropdownRef}>
          <button
            type="button"
            ref={bellRef}
            className={`header-notifications${unreadCount > 0 ? ' has-unread' : ''}`}
            onClick={handleBellClick}
            aria-label={bellLabel}
            aria-expanded={dropdownOpen}
            aria-haspopup="dialog"
            data-tip="Notifications"
          >
            <Icon name="bell-simple" size={20} className="icon" />
          </button>
          <NotificationsPopover
            open={dropdownOpen}
            notifications={notifications}
            onClose={handleCloseNotifications}
            onMarkRead={handleMarkRead}
            onMarkAllRead={handleMarkAllRead}
          />
        </div>
        )}
        <div className="usermenu" ref={accountRef}>
          {/* PRD-09: the whole account menu is gated on `account-menu`, matching the
              backend gate on the profile/personalization writes. Flag off = display-only
              avatar + sidebar logout stays (clean pre-PRD-09 rollback). */}
          <button
            type="button"
            className="usermenu-trigger"
            onClick={() => accountMenuFull && setAccountOpen((p) => !p)}
            aria-haspopup={accountMenuFull ? 'menu' : undefined}
            aria-expanded={accountMenuFull ? accountOpen : undefined}
            aria-label="Account menu"
            style={accountMenuFull ? undefined : { cursor: 'default' }}
          >
            <div
              className="avatar"
              aria-hidden="true"
              style={avatarUrl ? undefined : initials ? initialsStyle(displayName, avatarColor) : undefined}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className={avatarKind === 'logo' ? 'avatar-logo' : undefined} />
              ) : initials ? (
                <span className="avatar-initials">{initials}</span>
              ) : (
                <Icon name="user" size={20} className="avatar-icon" />
              )}
            </div>
            <div className="user-text">
              <div className="user-name">{displayName}</div>
              {titleText && <div className="user-role">{titleText}</div>}
            </div>
          </button>
          {accountMenuFull && accountOpen && (
            <div className="usermenu-panel" role="menu">
              <button
                type="button"
                role="menuitem"
                className="usermenu-item"
                onClick={() => {
                  setAccountOpen(false);
                  setShowHelp(true);
                }}
              >
                <Icon name="question" size={20} className="icon" />
                Get help
              </button>
              {showUpdateProfile && (
                <button
                  type="button"
                  role="menuitem"
                  className="usermenu-item"
                  onClick={() => {
                    setAccountOpen(false);
                    setShowProfile(true);
                  }}
                >
                  <Icon name="user-circle" size={20} className="icon" />
                  Update profile
                </button>
              )}
              {canSwitchAccount && (
                <button
                  type="button"
                  role="menuitem"
                  className="usermenu-item"
                  onClick={() => {
                    setAccountOpen(false);
                    navigate('/settings');
                  }}
                >
                  <Icon name="arrows-left-right" size={20} className="icon" />
                  Switch account
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="usermenu-item usermenu-item--danger"
                onClick={() => {
                  setAccountOpen(false);
                  onLogout?.();
                }}
              >
                <Icon name="sign-out" size={20} className="icon" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      <EditProfileModal
        isOpen={showProfile && showUpdateProfile}
        user={user}
        onClose={() => setShowProfile(false)}
        onSaved={() => onRefreshUser?.()}
        onShowNotification={onShowNotification}
      />
      <GetHelpModal
        isOpen={showHelp}
        user={user}
        pageTitle={pageTitle}
        onClose={() => setShowHelp(false)}
        onShowNotification={onShowNotification}
      />
    </header>
  );
};

export default AppHeader;
